import React from 'react';
import { SectionId, SocialId } from '../types';

type IconProps = {
  size?: number;
  className?: string;
};

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
});

export const ProjectsIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M4 8.5 12 4l8 4.5v7L12 20l-8-4.5z" />
    <circle cx="12" cy="11" r="2" />
  </svg>
);

export const StackIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m12 3 8.5 4.5L12 12 3.5 7.5z" />
    <path d="m3.5 12 8.5 4.5 8.5-4.5M3.5 16.5 12 21l8.5-4.5" />
  </svg>
);

export const AboutIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 8h.01M12 11v5" />
  </svg>
);

export const ContactIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <rect x="3.5" y="6" width="17" height="12" rx="2" />
    <path d="m4 7.5 8 5.5 8-5.5" />
  </svg>
);

export const PulseIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M3 12h3l2-5 3 10 2.5-7 1.8 4H21" />
  </svg>
);

export const RepoIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M6 3.5h12a1 1 0 0 1 1 1V20H6.5A2.5 2.5 0 0 1 4 17.5v-11A2.5 2.5 0 0 1 6.5 4" />
    <path d="M6.5 16.5H19M9 7.5h6" />
  </svg>
);

export const ClockIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 1.8" />
  </svg>
);

export const CheckIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </svg>
);

export const MailIcon = ContactIcon;

export const CopyIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" />
  </svg>
);

export const DiamondIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m12 4 8 8-8 8-8-8z" />
  </svg>
);

/** Card badge: this project is running somewhere you can open right now. */
export const LiveDotIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="4" fill="currentColor" stroke="none" />
    <path d="M5.5 5.5a9 9 0 0 0 0 13M18.5 5.5a9 9 0 0 1 0 13" opacity="0.55" />
  </svg>
);

/** Nav: the reviews section. A speech bubble, not a star — nobody rated me. */
export const BlogIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M5 4h9l5 5v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
    <path d="M14 4v5h5M8 13h8M8 17h5" />
  </svg>
);

export const ArrowLeftIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M19 12H5m6-7-7 7 7 7" />
  </svg>
);

export const ReviewsIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M20 14.5a2 2 0 0 1-2 2H8l-4 3.5v-14a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    <path d="M8.5 8.5h7M8.5 12h4.5" />
  </svg>
);

/** Card badge: still being built, so there is nothing to open yet. */
export const WipIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M12 7.5V12l3 2" />
  </svg>
);

/** Card badge: the source of this project is public. */
export const CodeIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m8.5 8-4.5 4 4.5 4M15.5 8l4.5 4-4.5 4" />
  </svg>
);

export const ArrowRightIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

export const CloseIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="m6 6 12 12M18 6 6 18" />
  </svg>
);

export const MenuIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const GridViewIcon = ({ size = 18, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <rect x="4" y="4" width="7" height="7" rx="1.6" />
    <rect x="13" y="4" width="7" height="7" rx="1.6" />
    <rect x="4" y="13" width="7" height="7" rx="1.6" />
    <rect x="13" y="13" width="7" height="7" rx="1.6" />
  </svg>
);

export const ListViewIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M4 12h11" />
    <path d="M9 8v8" />
    <circle cx="18" cy="12" r="2" />
  </svg>
);

export const SunIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <circle cx="12" cy="12" r="3.6" />
    <path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6 17 7M7 17l-1.4 1.4" />
  </svg>
);

export const MoonIcon = ({ size = 18, className }: IconProps) => (
  <svg {...base(size)} className={className} aria-hidden>
    <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z" />
  </svg>
);

export const QuoteIcon = ({ size = 34, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M9.6 6c-3 1.4-4.8 4-4.8 7v5h6.4v-6.4H7.6c.2-1.8 1.2-3.2 3-4.2zm9.6 0c-3 1.4-4.8 4-4.8 7v5h6.4v-6.4h-3.6c.2-1.8 1.2-3.2 3-4.2z" />
  </svg>
);

export const GithubIcon = ({ size = 20, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M12 2a10 10 0 0 0-3.2 19.5c.5.1.7-.2.7-.5v-1.8c-2.8.6-3.4-1.3-3.4-1.3-.5-1.2-1.1-1.5-1.1-1.5-.9-.6.1-.6.1-.6 1 .1 1.5 1 1.5 1 .9 1.5 2.4 1.1 3 .8.1-.7.4-1.1.6-1.4-2.2-.3-4.6-1.1-4.6-5 0-1.1.4-2 1-2.7-.1-.3-.4-1.3.1-2.7 0 0 .8-.3 2.7 1a9.4 9.4 0 0 1 5 0c1.9-1.3 2.7-1 2.7-1 .5 1.4.2 2.4.1 2.7.6.7 1 1.6 1 2.7 0 3.9-2.4 4.7-4.6 5 .4.3.7 1 .7 2v3c0 .3.2.6.7.5A10 10 0 0 0 12 2z" />
  </svg>
);

export const TelegramIcon = ({ size = 20, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M21.7 4.3 2.9 11.5c-1 .4-1 1.7.1 2l4.7 1.4 1.8 5.4c.3.8 1.3 1 1.9.4l2.5-2.4 4.7 3.4c.7.5 1.7.2 1.9-.7l3-14.6c.2-1-.8-1.8-1.8-1.4zM9.4 14.2l8.8-5.5-7.2 6.6-.3 3z" />
  </svg>
);

/**
 * Kwork has no glyph in any icon set, so the mark is the platform's initial
 * in the same monochrome weight as the others — a wordmark here would sit at
 * a different visual density than GitHub and Telegram next to it.
 */
export const KworkIcon = ({ size = 20, className }: IconProps) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path
      fillRule="evenodd"
      d="M4 2h16a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2zm3 4.5h2.4v4.1l3.6-4.1h3l-4.3 4.8 4.5 6.2h-2.9l-3.2-4.5-.7.8v3.7H7z"
    />
  </svg>
);

/**
 * Icons are the one visual thing content.ts cannot hold, so entries there are
 * matched to an icon by id here.
 */
export const navIcons: Record<SectionId, typeof ProjectsIcon> = {
  projects: ProjectsIcon,
  stack: StackIcon,
  blog: BlogIcon,
  about: AboutIcon,
  testimonials: ReviewsIcon,
  contact: ContactIcon,
};

export const socialIcons: Record<SocialId, typeof GithubIcon> = {
  github: GithubIcon,
  telegram: TelegramIcon,
  kwork: KworkIcon,
};
